/**
 * Order fulfillment modal — Phase 18.
 *
 * Two modes:
 * - "edit"  : adjust the quantities the warehouse will actually deliver
 *             (partial fulfillment when out of stock, etc.)
 * - "pack"  : packing checklist — enter the number of pieces really placed
 *             in the carton, with cross-check against the fulfilled qty.
 *             Save is blocked until every product matches or the admin
 *             explicitly acknowledges a discrepancy.
 *
 * Each item now shows image + brand + category + barcode to avoid mistakes
 * between perfumes that share the same name across categories.
 */
import { useState, useEffect, useMemo } from 'react';
import { prepSortItems } from '../utils/itemSorting';
import {
  X, Save, AlertTriangle, Check, Minus, ClipboardCheck, Pencil,
  Barcode, Package as PkgIcon, Plus, Search, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IMG_FALLBACK = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=160';

const OrderFulfillmentModal = ({ order, onClose, onUpdated }) => {
  const [items, setItems] = useState([]);
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('edit'); // 'edit' | 'pack'
  const [productDetails, setProductDetails] = useState({}); // {productId: {image_url, category, barcode}}
  const [ackDiscrepancy, setAckDiscrepancy] = useState(false);

  // Initialise items from order
  useEffect(() => {
    if (order?.items) {
      setItems(prepSortItems(order.items).map(item => ({
        ...item,
        original_quantity: item.original_quantity || item.quantity,
        fulfilled_quantity: item.quantity,
        packed_count: 0, // verified count placed in carton
        unit_price_override: null, // Phase 36 — admin discount on this line
        original_unit_price: item.unit_price,
      })));
    }
  }, [order]);

  // Fetch product details (image, category, barcode) in parallel so the
  // preparer can disambiguate similarly-named perfumes.
  useEffect(() => {
    if (!order?.items?.length) return;
    const ids = [...new Set(order.items.map(it => it.product_id))];
    Promise.all(
      ids.map(id =>
        axios.get(`${API_URL}/api/products/${id}`).then(r => [id, r.data]).catch(() => [id, null])
      )
    ).then(pairs => {
      const map = {};
      pairs.forEach(([id, p]) => {
        if (p) map[id] = {
          image_url: p.image_url,
          category: p.category,
          barcode: p.barcode,
          brand: p.brand,
          price_detail: p.price_detail,
          price_semi_gros: p.price_semi_gros,
          price_gros: p.price_gros,
          cost_price: p.cost_price,
          vip_margin: p.vip_margin,
        };
      });
      setProductDetails(map);
    });
  }, [order?.id, order?.items]);

  const updateQuantity = (idx, newQty) => {
    // Phase 39 — admin can push the line ABOVE the originally ordered qty
    // (e.g. customer asks for more during preparation). Stock is deducted
    // server-side; the max is just a soft limit removed here.
    const val = Math.max(0, newQty);
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, fulfilled_quantity: val } : it
    ));
  };

  const updatePacked = (idx, newCount) => {
    const val = Math.max(0, newCount);
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, packed_count: val } : it
    ));
  };

  const updateUnitPrice = (idx, raw) => {
    // Empty string → revert to original tier-based price.
    if (raw === '' || raw === null) {
      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, unit_price_override: null, unit_price: it.original_unit_price } : it
      ));
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) return;
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, unit_price_override: n, unit_price: n } : it
    ));
  };

  const newTotal = useMemo(
    () => items.reduce((s, it) => s + (it.unit_price * it.fulfilled_quantity), 0),
    [items]
  );
  const originalTotal = order?.subtotal || order?.total || 0;
  const hasChanges = items.some(it =>
    it.is_new || it.fulfilled_quantity !== it.quantity || it.unit_price_override != null || it.tier_override
  );

  // Prix par palier d'un produit (détail / semi-gros / gros / VIP = achat + marge).
  const tierPrices = (p) => {
    if (!p) return {};
    const out = { detail: p.price_detail, semi_gros: p.price_semi_gros, gros: p.price_gros };
    const cp = Number(p.cost_price) || 0;
    if (cp > 0) out.vip = Math.round((cp + (Number(p.vip_margin) || 1)) * 100) / 100;
    return out;
  };

  const updateTier = (idx, tier) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      if (!tier) return { ...it, tier_override: null, unit_price_override: null, unit_price: it.original_unit_price };
      const price = tierPrices(productDetails[it.product_id])[tier];
      if (price == null) return it;
      return { ...it, tier_override: tier, unit_price_override: null, unit_price: price };
    }));
  };

  // Packing reconciliation
  const packingStatus = useMemo(() => {
    const mismatches = items.filter(
      it => it.fulfilled_quantity > 0 && it.packed_count !== it.fulfilled_quantity
    );
    const allMatch = mismatches.length === 0 &&
      items.every(it => it.fulfilled_quantity === 0 || it.packed_count > 0);
    return { mismatches, allMatch };
  }, [items]);

  const [extraAmount, setExtraAmount] = useState(order?.extra_amount || '');
  const [extraLabel, setExtraLabel] = useState(order?.extra_label || '');

  // Phase 39 — admin can search & add new products to an existing order
  // during preparation (walk-up additions, missed catalogue items, etc.).
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products`, {
          params: { search: term, in_stock: true },
        });
        if (cancelled) return;
        const existingIds = new Set(items.map((i) => i.product_id));
        // Cap to 8 to keep the dropdown light.
        setSearchResults(
          (res.data || []).filter((p) => !existingIds.has(p.id)).slice(0, 8)
        );
      } catch (_) {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchTerm, items]);

  // Tarif de la commande (VIP / gros / semi-gros forcé au POS ou via le
  // compte client) pour prévisualiser le prix des nouvelles lignes.
  const orderTier = useMemo(() => {
    if (order?.pricing_override) return order.pricing_override;
    const tiers = new Set((order?.items || []).filter(i => i.quantity > 0).map(i => String(i.tier || '').replace('_override', '')));
    if (tiers.size !== 1) return null;
    const t = [...tiers][0];
    if (t === 'vip') return 'vip';
    if ((t === 'gros' || t === 'semi_gros') && ['pos', 'quote'].includes(order?.source)) return t;
    return null;
  }, [order]);

  const previewPrice = (product) => {
    if (orderTier === 'vip') {
      const cp = Number(product.cost_price) || 0;
      if (cp > 0) return { tier: 'vip', price: Math.round((cp + (Number(product.vip_margin) || 1)) * 100) / 100 };
      return { tier: 'gros', price: product.price_gros || product.price_detail || 0 };
    }
    if (orderTier === 'gros' || orderTier === 'semi_gros') {
      return { tier: orderTier, price: product[`price_${orderTier}`] || product.price_detail || 0 };
    }
    return { tier: 'detail', price: product.price_detail || 0 };
  };

  const addProduct = (product) => {
    // Append as a brand-new line. The final price is computed server-side
    // (same forced-tier rules); this is a faithful preview.
    const { tier, price } = previewPrice(product);
    setProductDetails(prev => ({ ...prev, [product.id]: { ...(prev[product.id] || {}), image_url: product.image_url, category: product.category, brand: product.brand,
      price_detail: product.price_detail, price_semi_gros: product.price_semi_gros, price_gros: product.price_gros, cost_price: product.cost_price, vip_margin: product.vip_margin } }));
    setItems((prev) => [
      ...prev,
      {
        product_id: product.id,
        product_name: product.name,
        brand: product.brand || '',
        category: product.category || '',
        quantity: 0,
        original_quantity: 0,
        fulfilled_quantity: 1,
        packed_count: 0,
        unit_price: price,
        unit_price_override: null,
        original_unit_price: price,
        tier,
        total: price,
        is_new: true,
      },
    ]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const removeLine = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSave = mode === 'edit'
    ? (hasChanges || extraAmount !== (order?.extra_amount || '') || extraLabel !== (order?.extra_label || ''))
    : (packingStatus.allMatch || ackDiscrepancy);

  const handleSave = async () => {
    // Phase 37: in "pack" mode, treat the packed_count as the authoritative
    // quantity actually shipped — if it differs from fulfilled_quantity, this
    // is what the customer will be charged for. Always ask confirmation when
    // there's a discrepancy to avoid an accidental shorted order.
    const itemsForSave = items.map((it) => {
      const qty = mode === 'pack' && it.fulfilled_quantity > 0
        ? Math.max(0, Math.min(it.packed_count, it.fulfilled_quantity))
        : it.fulfilled_quantity;
      return {
        product_id: it.product_id,
        fulfilled_quantity: qty,
        ...(it.unit_price_override != null ? { unit_price_override: it.unit_price_override } : {}),
        ...(it.tier_override ? { tier_override: it.tier_override } : {}),
        ...(it.is_new ? { is_new: true } : {}),
      };
    });

    if (mode === 'pack') {
      const discrepancies = items.filter(
        (it) => it.fulfilled_quantity > 0 && it.packed_count !== it.fulfilled_quantity,
      );
      if (discrepancies.length > 0) {
        const summary = discrepancies
          .slice(0, 5)
          .map((it) => `• ${it.product_name || 'Produit'}: ${it.fulfilled_quantity} → ${it.packed_count}`)
          .join('\n');
        const more = discrepancies.length > 5 ? `\n…et ${discrepancies.length - 5} autres` : '';
        const ok = window.confirm(
          `${discrepancies.length} écart(s) entre prévu et emballé :\n\n${summary}${more}\n\n` +
          'Mettre à jour la commande avec les quantités RÉELLEMENT emballées ?\n' +
          '(Le client sera facturé selon le carton, pas selon la commande initiale.)'
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        items: itemsForSave,
        admin_note: adminNote || null,
        ...(extraAmount && parseFloat(extraAmount) > 0
          ? { extra_amount: parseFloat(extraAmount), extra_label: extraLabel.trim() || 'Frais divers' }
          : {}),
      };
      const response = await axios.put(
        `${API_URL}/api/orders/${order.id}/items`, payload
      );
      toast.success(mode === 'pack' ? 'Préparation validée' : 'Commande mise à jour');
      onUpdated(response.data);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Erreur lors de la mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  const getEditStatus = (it) => {
    if (it.is_new) return { label: 'Ajouté', color: 'text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)] border-[color:rgb(var(--primary-rgb)/0.40)]' };
    if (it.fulfilled_quantity === 0) return { label: 'Indisponible', color: 'text-red-500 bg-red-500/10 border-red-500/30' };
    if (it.fulfilled_quantity > it.original_quantity) return { label: `+${it.fulfilled_quantity - it.original_quantity} ajouté(s)`, color: 'text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)] border-[color:rgb(var(--primary-rgb)/0.40)]' };
    if (it.fulfilled_quantity < it.original_quantity) return { label: 'Partiel', color: 'text-amber-500 bg-amber-500/10 border-amber-500/30' };
    return { label: 'Complet', color: 'text-green-500 bg-green-500/10 border-green-500/30' };
  };

  const getPackStatus = (it) => {
    if (it.fulfilled_quantity === 0) return null; // not applicable
    if (it.packed_count === 0) return { label: 'À vérifier', color: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/30' };
    if (it.packed_count < it.fulfilled_quantity) return { label: `Manque ${it.fulfilled_quantity - it.packed_count}`, color: 'text-red-500 bg-red-500/10 border-red-500/30' };
    if (it.packed_count > it.fulfilled_quantity) return { label: `+${it.packed_count - it.fulfilled_quantity} en trop`, color: 'text-red-500 bg-red-500/10 border-red-500/30' };
    return { label: '✓ OK', color: 'text-green-500 bg-green-500/10 border-green-500/30' };
  };

  const onImgErr = (e) => { e.target.src = IMG_FALLBACK; };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="fulfillment-modal">
      <div className="bg-[color:var(--color-surface)] border border-white/10 w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {mode === 'pack' ? 'Préparation du carton' : 'Modifier les quantités'}
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Commande #{order?.id?.slice(-8).toUpperCase()}
              {mode === 'pack' && items.length > 0 && (
                <span className="ml-2 text-[color:var(--color-primary)]">
                  · {items.filter(it => it.packed_count === it.fulfilled_quantity && it.fulfilled_quantity > 0).length}
                  / {items.filter(it => it.fulfilled_quantity > 0).length} vérifiés
                </span>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-2 text-zinc-500 hover:text-white transition-colors"
            data-testid="close-fulfillment-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-white/10">
          <button type="button" onClick={() => setMode('edit')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
              mode === 'edit' ? 'text-[color:var(--color-primary)] border-b-2 border-[color:var(--color-primary)]' : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="mode-edit-btn">
            <Pencil className="w-4 h-4" />
            Quantités livrables
          </button>
          <button type="button" onClick={() => setMode('pack')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
              mode === 'pack' ? 'text-[color:var(--color-primary)] border-b-2 border-[color:var(--color-primary)]' : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="mode-pack-btn">
            <ClipboardCheck className="w-4 h-4" />
            Préparation du carton
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.map((it, idx) => {
            const editStatus = getEditStatus(it);
            const packStatus = getPackStatus(it);
            const details = productDetails[it.product_id] || {};
            const brandLabel = (it.brand || '').trim() || 'Autres';
            const prevBrand = idx > 0 ? ((items[idx - 1].brand || '').trim() || 'Autres') : null;
            const showBrandHeader = idx === 0 || prevBrand !== brandLabel;
            return (
              <div key={`fulfillment-${it.product_id}`}>
                {showBrandHeader && (
                  <div className="flex items-center gap-2 mb-2" data-testid={`prep-brand-header-${idx}`}>
                    <span className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] font-medium">
                      {brandLabel}
                    </span>
                    <div className="flex-1 h-px bg-[color:rgb(var(--primary-rgb)/0.20)]" />
                  </div>
                )}
              <div
                className="bg-[color:var(--color-surface)] border border-white/5 p-4"
                data-testid={`fulfillment-item-${idx}`}>
                {/* Product header — image + identifiers */}
                <div className="flex gap-4 mb-3">
                  <div className="w-16 h-16 bg-[color:var(--color-bg)] overflow-hidden flex-shrink-0 border border-white/5">
                    <img
                      src={details.image_url || IMG_FALLBACK}
                      alt={it.product_name}
                      className="w-full h-full object-cover"
                      onError={onImgErr}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium leading-tight">
                      {it.product_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {(it.brand || details.brand) && (
                        <span className="text-[10px] uppercase tracking-wider bg-[color:rgb(var(--primary-rgb)/0.10)] text-[color:var(--color-primary)] px-2 py-0.5 border border-[color:rgb(var(--primary-rgb)/0.20)]">
                          {it.brand || details.brand}
                        </span>
                      )}
                      {details.category && (
                        <span className="text-[10px] uppercase tracking-wider bg-purple-500/10 text-purple-400 px-2 py-0.5 border border-purple-500/20 flex items-center gap-1">
                          <PkgIcon className="w-2.5 h-2.5" />
                          {details.category}
                        </span>
                      )}
                      {details.barcode && (
                        <span className="text-[10px] tracking-wider bg-white/5 text-zinc-400 px-2 py-0.5 border border-white/10 flex items-center gap-1 font-mono">
                          <Barcode className="w-2.5 h-2.5" />
                          {details.barcode}
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-500 text-xs mt-1.5">
                      {it.is_new ? (
                        <span className="text-[color:var(--color-primary)]">Nouveau · </span>
                      ) : (
                        <>Commandé : <span className="text-zinc-300">{it.original_quantity}</span> · </>
                      )}
                      {it.unit_price?.toFixed(2)}€/unité ({it.tier})
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs border h-fit flex-shrink-0 ${
                    mode === 'pack' && packStatus ? packStatus.color : editStatus.color
                  }`} data-testid={`item-status-${idx}`}>
                    {mode === 'pack' && packStatus ? packStatus.label : editStatus.label}
                  </span>
                  {mode === 'edit' && it.is_new && (
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      title="Retirer ce produit de la commande"
                      data-testid={`remove-new-line-${idx}`}
                      className="p-1.5 text-zinc-500 hover:text-red-500 transition-colors h-fit flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* EDIT MODE: fulfilled quantity */}
                {mode === 'edit' && (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 text-xs uppercase tracking-wider">Quantité livrable :</span>
                      <div className="flex items-center gap-1">
                        <button type="button"
                          onClick={() => updateQuantity(idx, it.fulfilled_quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors"
                          data-testid={`qty-decrease-${idx}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <input type="number" min="0"
                          value={it.fulfilled_quantity}
                          onChange={(e) => updateQuantity(idx, parseInt(e.target.value) || 0)}
                          className="w-16 text-center bg-transparent border border-white/10 text-white text-sm py-1 outline-none focus:border-[color:var(--color-primary)]"
                          data-testid={`qty-input-${idx}`} />
                        <button type="button"
                          onClick={() => updateQuantity(idx, it.fulfilled_quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors"
                          data-testid={`qty-increase-${idx}`}>
                          <span className="text-sm">+</span>
                        </button>
                      </div>
                      <span className="text-zinc-600 text-xs">
                        {it.is_new ? '(nouveau)' : `/ ${it.original_quantity}`}
                      </span>
                    </div>
                    {/* Inline unit price override (Phase 36) + tier selector */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {(() => {
                        const tp = tierPrices(productDetails[it.product_id]);
                        const labels = { detail: 'Détail', semi_gros: 'Semi-gros', gros: 'Gros', vip: 'VIP' };
                        return (
                          <select
                            value={it.tier_override || ''}
                            onChange={(e) => updateTier(idx, e.target.value || null)}
                            title="Choisir le tarif appliqué à cette ligne"
                            data-testid={`tier-select-${idx}`}
                            className={`bg-[color:var(--color-surface)] border text-xs py-1 px-2 outline-none focus:border-[color:var(--color-primary)] ${
                              it.tier_override ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]' : 'border-white/10 text-zinc-300'
                            }`}
                          >
                            <option value="">{`Tarif : ${String(it.tier || 'auto').replace('_override', '')}`}</option>
                            {['detail', 'semi_gros', 'gros', 'vip'].filter(t => tp[t] != null).map(t => (
                              <option key={t} value={t}>{`${labels[t]} — ${Number(tp[t]).toFixed(2)}€`}</option>
                            ))}
                          </select>
                        );
                      })()}
                      <span className="text-zinc-500 text-xs uppercase tracking-wider">Prix €/u :</span>
                      <input
                        type="number" step="0.01" min="0"
                        value={it.unit_price_override ?? it.unit_price ?? ''}
                        onChange={(e) => updateUnitPrice(idx, e.target.value)}
                        title="Modifier le prix unitaire pour appliquer une réduction"
                        data-testid={`unit-price-input-${idx}`}
                        className={`w-20 text-center bg-transparent border text-sm py-1 outline-none focus:border-[color:var(--color-primary)] ${
                          it.unit_price_override != null && it.unit_price_override !== it.original_unit_price
                            ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] font-medium'
                            : 'border-white/10 text-white'
                        }`}
                      />
                      {it.unit_price_override != null && it.unit_price_override !== it.original_unit_price && (
                        <button
                          type="button"
                          onClick={() => updateUnitPrice(idx, '')}
                          title="Restaurer le prix initial"
                          data-testid={`unit-price-reset-${idx}`}
                          className="text-zinc-500 hover:text-white text-xs"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* PACK MODE: counted pieces */}
                {mode === 'pack' && it.fulfilled_quantity > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-500 text-xs uppercase tracking-wider">Mis en carton :</span>
                    <div className="flex items-center gap-1">
                      <button type="button"
                        onClick={() => updatePacked(idx, it.packed_count - 1)}
                        className="w-9 h-9 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors"
                        data-testid={`pack-decrease-${idx}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <input type="number" min="0"
                        value={it.packed_count}
                        onChange={(e) => updatePacked(idx, parseInt(e.target.value) || 0)}
                        className={`w-20 text-center bg-transparent border text-white text-lg py-1.5 outline-none ${
                          it.packed_count === it.fulfilled_quantity
                            ? 'border-green-500/40 focus:border-green-500'
                            : it.packed_count === 0
                            ? 'border-white/10 focus:border-[color:var(--color-primary)]'
                            : 'border-red-500/40 focus:border-red-500'
                        }`}
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                        data-testid={`pack-input-${idx}`} />
                      <button type="button"
                        onClick={() => updatePacked(idx, it.packed_count + 1)}
                        className="w-9 h-9 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors"
                        data-testid={`pack-increase-${idx}`}>
                        <span className="text-sm">+</span>
                      </button>
                    </div>
                    <span className="text-zinc-500 text-sm">/ {it.fulfilled_quantity} attendu</span>
                    {it.packed_count === it.fulfilled_quantity && (
                      <Check className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                )}
              </div>
              </div>
            );
          })}

          {/* Phase 39 — Add a brand-new product to the order (edit mode only).
              Useful when the customer asks for something extra at the door
              or the preparer notices a missing item from the call. */}
          {mode === 'edit' && (
            <div className="border border-[color:rgb(var(--primary-rgb)/0.20)] bg-[color:rgb(var(--primary-rgb)/0.05)] p-4 relative">
              <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">
                Ajouter un produit à la commande
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom, marque, code-barres…"
                  data-testid="add-product-search"
                  className="w-full bg-transparent border border-white/10 text-white text-sm pl-9 pr-3 py-2 outline-none focus:border-[color:var(--color-primary)]"
                />
              </div>
              {searchTerm.trim().length >= 2 && (
                <div className="mt-2 max-h-64 overflow-y-auto border border-white/10 bg-[color:var(--color-bg)]" data-testid="add-product-results">
                  {searching && (
                    <p className="text-zinc-500 text-xs p-3">Recherche…</p>
                  )}
                  {!searching && searchResults.length === 0 && (
                    <p className="text-zinc-500 text-xs p-3">Aucun produit trouvé (ou déjà dans la commande).</p>
                  )}
                  {searchResults.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => addProduct(p)}
                      className="w-full flex items-center gap-3 p-2 hover:bg-white/5 text-left transition-colors border-b border-white/5 last:border-b-0"
                      data-testid={`add-product-${p.id}`}
                    >
                      <img
                        src={p.image_url || IMG_FALLBACK}
                        alt={p.name}
                        onError={onImgErr}
                        className="w-10 h-10 object-cover bg-[color:var(--color-surface)] flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{p.name}</p>
                        <p className="text-zinc-500 text-xs truncate">
                          {p.brand ? `${p.brand} · ` : ''}{p.category || ''} · Stock {p.stock}
                        </p>
                      </div>
                      <span className="text-[color:var(--color-primary)] text-xs flex-shrink-0">
                        {p.price_detail?.toFixed(2)}€
                      </span>
                      <Plus className="w-4 h-4 text-[color:var(--color-primary)] flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin Note (edit mode only) */}
          {mode === 'edit' && (
            <div>
              <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">
                Note admin (visible par le client)
              </label>
              <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                rows={2} placeholder="Ex: Rupture de stock partielle sur certains articles..."
                className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)] resize-none"
                data-testid="admin-note-input" />
            </div>
          )}

          {/* Extra amount (edit mode only) — for products / fees that aren't
              priced per line, e.g. carrier surcharge or a small gift. */}
          {mode === 'edit' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-white/5 pt-4">
              <div className="sm:col-span-2">
                <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">
                  Frais / supplément (libellé)
                </label>
                <input type="text" value={extraLabel}
                  onChange={(e) => setExtraLabel(e.target.value)}
                  placeholder="Ex: Frais de livraison, Échantillons offerts, Supplément emballage"
                  data-testid="extra-label-input"
                  className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">
                  Montant € (à ajouter)
                </label>
                <input type="number" step="0.01" min="0" value={extraAmount}
                  onChange={(e) => setExtraAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="extra-amount-input"
                  className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]" />
              </div>
            </div>
          )}

          {/* Pack mode acknowledgement when discrepancies remain */}
          {mode === 'pack' && packingStatus.mismatches.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/30 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400 text-sm font-medium">
                    {packingStatus.mismatches.length} produit{packingStatus.mismatches.length > 1 ? 's' : ''} avec écart de comptage
                  </p>
                  <p className="text-zinc-400 text-xs mt-1">
                    Vérifiez physiquement chaque produit avant de valider. En cas d'écart confirmé, cochez la case ci-dessous pour autoriser la validation.
                  </p>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input type="checkbox" checked={ackDiscrepancy}
                      onChange={(e) => setAckDiscrepancy(e.target.checked)}
                      className="accent-[color:var(--color-primary)]"
                      data-testid="ack-discrepancy" />
                    <span className="text-zinc-300 text-sm">
                      J'ai vérifié et je confirme les quantités malgré l'écart
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-6">
          {/* Status summary */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm">
              {mode === 'edit' && newTotal < originalTotal && (
                <div className="flex items-center gap-2 text-amber-500">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Total modifié de {originalTotal.toFixed(2)}€ à {newTotal.toFixed(2)}€</span>
                </div>
              )}
              {mode === 'edit' && newTotal === originalTotal && (
                <div className="flex items-center gap-2 text-green-500">
                  <Check className="w-4 h-4" />
                  <span>Commande complète — {newTotal.toFixed(2)}€</span>
                </div>
              )}
              {mode === 'pack' && packingStatus.allMatch && (
                <div className="flex items-center gap-2 text-green-500" data-testid="pack-ok-banner">
                  <Check className="w-4 h-4" />
                  <span>Tous les produits vérifiés — carton prêt à expédier</span>
                </div>
              )}
              {mode === 'pack' && !packingStatus.allMatch && packingStatus.mismatches.length === 0 && (
                <span className="text-zinc-500">Saisissez le comptage de chaque produit</span>
              )}
            </div>
            {mode === 'edit' && (
              <span className="text-2xl text-[color:var(--color-primary)] font-light"
                style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="new-total">
                {newTotal.toFixed(2)}€
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-3 border border-white/10 text-zinc-400 hover:border-white/20 transition-colors text-sm"
              data-testid="cancel-fulfillment">
              Annuler
            </button>
            <button type="button" onClick={handleSave}
              disabled={saving || !canSave}
              className="flex-1 flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-fulfillment">
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde...' : mode === 'pack' ? 'Valider la préparation' : 'Valider les modifications'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFulfillmentModal;
